// PaymentDetailsTab.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { PaymentLinkCard } from './PaymentDetailsTab/PaymentLinkCard';
import { CancelPaymentModal } from './PaymentDetailsTab/CancelPaymentModal';
import { DeletePaymentModal } from './PaymentDetailsTab/DeletePaymentModal';
import { usePaymentLinkModal } from 'hooks/usePaymentLinkModal';
import { toast } from 'sonner';
import { PaymentHeader } from './PaymentDetailsTab/PaymentHeader';
import { usePaymentLinks } from 'hooks/usePaymentLinks';
import { EmptyPaymentState } from './PaymentDetailsTab/EmptyPaymentState';
import { PaymentLinkModal } from './PaymentDetailsTab/PaymentLinkModal';
import { getLeadDetails } from 'services/applicantService';
import { getInvoiceData, deleteOfflinePayment } from 'services/paymentServices';
import { jsPDF } from 'jspdf';
import html2canvasPro from 'html2canvas-pro';
import { PaidInvoiceTemplate } from './PaymentDetailsTab/PaymentLinkModal/PaidInvoiceTemplate';
import { useRef } from 'react';
import PaymentDetailsModal from '../LeadActions/PaymentDetailsModal';
import { PaymentReceiptModal } from './PaymentDetailsTab/PaymentReceiptModal';
import PaymentVerifyModal from 'app/pages/verification/components/PaymentVerifyModal';
import { isUserAdmin } from 'utils/permissionsUtils';
// import { getApplicationForms } from 'services/paymentServices';

const PaymentDetailsTab = ({ isFeePayment, isMobile, progressData = {}, refreshProgressJourney, refreshLeadData, leadId, leadData, onPaymentSuccess, isActive = true, isReadOnly = false }) => {
  const isDuplicate = Boolean(
    isReadOnly ||
    leadData?.IsDuplicate ||
    leadData?.isDuplicate ||
    leadData?.ParentLeadId ||
    leadData?.parentLeadId
  );
  // Remove useParams since leadId is now passed as a prop
  // const { id: leadId } = useParams();
  // Custom hooks for state management
  const {
    transactions,
    feeSummary,
    loading,
    syncing,
    transactionSyncing,
    loadPaymentLinks,
    handleSyncAll,
    handleSyncTransaction,
    handleCancelLink,
    refreshPaymentLinksSilently,
  } = usePaymentLinks(leadId, refreshProgressJourney);
  const [isInternational, setIsInternational] = useState(false);

  const paymentLinkModal = usePaymentLinkModal(leadId, refreshProgressJourney, refreshPaymentLinksSilently, isFeePayment, isInternational, refreshLeadData);

  // Cancel modal state
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [cancelConfirmState, setCancelConfirmState] = useState('pending');
  const [selectedLinkToCancel, setSelectedLinkToCancel] = useState(null);
  const [isCancellingLink, setIsCancellingLink] = useState(null);

  // Delete modal state
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState('pending');
  const [selectedLinkToDelete, setSelectedLinkToDelete] = useState(null);
  const [isDeletingLink, setIsDeletingLink] = useState(null);

  // inside PaymentDetailsTab

  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(null); // stores PaymentLinkId
  const [invoiceData, setInvoiceData] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const invoiceRef = useRef(null);

  // Edit payment state
  const [editingPayment, setEditingPayment] = useState(null);

  const handleEditPayment = (transaction) => {
    setEditingPayment(transaction);
    setIsPaymentModalOpen(true);
  };

  // Receipt modal state
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedReceiptTransaction, setSelectedReceiptTransaction] = useState(null);
  const [receiptType, setReceiptType] = useState(1);

  // Verify modal state
  const isAdmin = isUserAdmin();
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [verifyModalLead, setVerifyModalLead] = useState(null);

  const handleOpenVerifyModal = (transaction) => {
    // Map transaction -> flat shape expected by PaymentVerifyModal
    const primaryPayment = Array.isArray(transaction?.Payments) && transaction.Payments.length > 0
      ? transaction.Payments[0]
      : {};
    setVerifyModalLead({
      PaymentId: primaryPayment?.PaymentId || transaction?.PaymentId,
      PaymentAmount: transaction?.Amount,
      PaymentMethod: primaryPayment?.PaymentMethod || primaryPayment?.PaymentGroup || transaction?.GatewayName,
      Attachments: primaryPayment?.Attachments,
      AttachmentPath: primaryPayment?.AttachmentPath,
      ReadyForVerification: transaction?.ReadyForVerification ?? primaryPayment?.ReadyForVerification ?? true,
      VerifyStatus: transaction?.VerifyStatus ?? primaryPayment?.VerifyStatus,
      RemarksForRejection: primaryPayment?.RemarksForRejection || transaction?.RemarksForRejection,
      FullName: transaction?.FullName,
      Email: transaction?.Email,
    });
    setIsVerifyModalOpen(true);
  };

  const handleVerifyModalActionComplete = () => {
    loadPaymentLinks();
    if (refreshLeadData) refreshLeadData();
    if (refreshProgressJourney) refreshProgressJourney();
  };

  // Application forms fallback state
  // const [applicationForms, setApplicationForms] = useState([]);



  // Segregate transactions based on IsEnrollmentFee flag
  const displayTransactions = useMemo(() => {
    if (!Array.isArray(transactions)) return [];

    if (isFeePayment) {
      // Fee Payment tab: show only enrollment fee links
      return transactions.filter(t => t.IsEnrollmentFee === true);
    } else {
      // Payment Details tab: show only non-enrollment fee links
      return transactions.filter(t => t.IsEnrollmentFee === false || t.IsEnrollmentFee === null || t.IsEnrollmentFee === undefined);
    }
  }, [transactions, isFeePayment]);

  const paidLinkExists = useMemo(
    () => displayTransactions.some(t => t.LinkStatus === 'PAID'),
    [displayTransactions]
  );

  // Derived state to check if accounts counsellor (Accounts User) is assigned
  const isAccountsAssigned = useMemo(() => {
    const counselorName = leadData?.accountsCounsellorName ||
      leadData?.SourceDetails?.LeadAccountantCounsellorName ||
      leadData?.accountsCounsellor ||
      leadData?.AccountantCounsellorName;

    if (!counselorName) return false;
    const n = counselorName.toString().trim().toLowerCase();
    // Check against various unassigned indicators
    return !["", "n/a", "unassigned", "null", "undefined"].includes(n);
  }, [leadData]);

  // Check if previous journey steps are completed
  const canCreatePaymentLink = useMemo(() => {
    if (isFeePayment) {
      // Fee Payment requires accounts counsellor to be assigned
      return isAccountsAssigned;
    }
    // Application fee payment link can be created without specific assignment
    return true;
  }, [isFeePayment, isAccountsAssigned]);

  // Get descriptive message for why payment link cannot be created
  const paymentLinkBlockedReason = useMemo(() => {
    if (isFeePayment) {
      if (!isAccountsAssigned) {
        return "Assign Accounts User before creating link";
      }
    }
    return null;
  }, [isFeePayment, isAccountsAssigned]);

  useEffect(() => {
    if (leadId && isActive) {
      loadPaymentLinks();
      // Fetch lead details to determine international status
      const fetchLeadDetails = async () => {
        try {
          const res = await getLeadDetails(leadId);
          if (res.success) {
            const data = res.data || {};
            // Check IsInternationalLead flag or nationality (assume 1 is India)
            const isIntl = data.BasicInformation?.IsInternationalLead === true ||
              data.IsInternationalLead === true ||
              (data.BasicInformation?.NationalityId !== 1 && data.BasicInformation?.NationalityId !== undefined && data.BasicInformation?.NationalityId !== null) ||
              (data.NationalityId !== 1 && data.NationalityId !== undefined && data.NationalityId !== null);
            setIsInternational(isIntl);
          }
        } catch (err) {
          console.error('Error fetching lead details for international check:', err);
        }
      };
      fetchLeadDetails();

      // Fetch application forms for fallback
      // const loadForms = async () => {
      //   try {
      //     const res = await getApplicationForms();
      //     if (res.success) {
      //       setApplicationForms(Array.isArray(res.data) ? res.data : []);
      //     }
      //   } catch (err) {
      //     console.error('Error fetching application forms for fallback:', err);
      //   }
      // };
      // loadForms();
    }
  }, [leadId, isActive]);

  const handleOpenPaymentLinkModal = () => {
    if (isDuplicate) return;

    // Check if previous journey steps are completed
    if (!canCreatePaymentLink) {
      toast.error(paymentLinkBlockedReason || 'Please complete previous steps in the journey before generating a payment link.');
      return;
    }

    const hasActiveLink = displayTransactions.some(txn => txn.LinkStatus === 'ACTIVE');

    if (hasActiveLink) {
      toast.error('Cannot create a new payment link. An active payment link already exists.');
      return;
    }

    setIsPaymentModalOpen(true);
  };
  const handleOpenCancelConfirm = (transaction) => {
    if (transaction.LinkStatus !== 'ACTIVE') {
      toast.error('Only active payment links can be cancelled');
      return;
    }
    setSelectedLinkToCancel(transaction);
    setCancelConfirmState('pending');
    setShowCancelConfirmModal(true);
  };

  const handleConfirmCancelPayment = async () => {
    if (!selectedLinkToCancel) return;

    setIsCancellingLink(selectedLinkToCancel.PaymentLinkId);
    const result = await handleCancelLink(selectedLinkToCancel);

    if (result.success) {
      setCancelConfirmState('success');
      setTimeout(() => {
        setShowCancelConfirmModal(false);
        setSelectedLinkToCancel(null);
        setIsCancellingLink(null);
      }, 1500);
    } else {
      setIsCancellingLink(null);
    }
  };

  const handleCloseCancelConfirm = () => {
    if (cancelConfirmState !== 'pending') return;
    setShowCancelConfirmModal(false);
    setSelectedLinkToCancel(null);
    setIsCancellingLink(null);
  };

  const handleOpenDeleteConfirm = (transaction) => {
    setSelectedLinkToDelete(transaction);
    setDeleteConfirmState('pending');
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDeletePayment = async () => {
    if (!selectedLinkToDelete) return;

    setIsDeletingLink(selectedLinkToDelete.PaymentLinkId);
    try {
      const result = await deleteOfflinePayment(selectedLinkToDelete.PaymentLinkId);

      if (result.success) {
        setDeleteConfirmState('success');
        // Refresh payments list & lead data
        setTimeout(() => {
          setShowDeleteConfirmModal(false);
          setSelectedLinkToDelete(null);
          setIsDeletingLink(null);
          loadPaymentLinks();
          if (refreshLeadData) refreshLeadData();
          if (refreshProgressJourney) refreshProgressJourney();
        }, 1500);
      } else {
        toast.error(result.error || 'Failed to delete payment');
        setIsDeletingLink(null);
      }
    } catch (err) {
      console.error('Delete payment error:', err);
      toast.error('Something went wrong while deleting payment');
      setIsDeletingLink(null);
    }
  };

  const handleCloseDeleteConfirm = () => {
    if (deleteConfirmState !== 'pending') return;
    setShowDeleteConfirmModal(false);
    setSelectedLinkToDelete(null);
    setIsDeletingLink(null);
  };

  const handleDownloadInvoice = async (transaction) => {
    if (!leadId) return;

    setIsDownloadingInvoice(transaction.PaymentLinkId);

    try {
      const res = await getInvoiceData(leadId);
      if (res.success && res.data) {
        setInvoiceData(res.data);

        // Wait for state update and template rendering
        setTimeout(async () => {
          if (!invoiceRef.current) {
            toast.error("Failed to generate invoice. Please try again.");
            setIsDownloadingInvoice(null);
            return;
          }

          try {
            const canvas = await html2canvasPro(invoiceRef.current, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.85);
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'px',
              format: [canvas.width / 2, canvas.height / 2],
              compress: true,
            });

            pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width / 2, canvas.height / 2, undefined, 'FAST');
            pdf.save(`Invoice_${res.data.FullName || 'Student'}_${transaction.PaymentLinkId.substring(0, 8)}.pdf`);
            toast.success("Invoice downloaded successfully");
          } catch (pdfErr) {
            console.error('PDF generation error:', pdfErr);
            toast.error("Failed to generate PDF");
          } finally {
            setIsDownloadingInvoice(null);
            setInvoiceData(null);
          }
        }, 500);
      } else {
        toast.error(res.message || "Failed to fetch invoice data");
        setIsDownloadingInvoice(null);
      }
    } catch (err) {
      console.error('Invoice download error:', err);
      toast.error("Something went wrong while downloading invoice");
      setIsDownloadingInvoice(null);
    }
  };

  const handleDownloadReceipt = (transaction, type) => {
    setSelectedReceiptTransaction(transaction);
    setReceiptType(type);
    setIsReceiptModalOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Hidden layout for PDF generation */}
      {invoiceData && (
        <div className="fixed -left-[9999px] -top-[9999px]">
          <PaidInvoiceTemplate
            ref={invoiceRef}
            invoiceData={invoiceData}
            leadData={leadData}
          />
        </div>
      )}

      {/* Compact Non-Sticky Payment Header with separate scroll for cards */}
      <div className="shrink-0 bg-white dark:bg-dark-900 -mx-4 px-4 pt-1 pb-2 border-b border-gray-100 dark:border-gray-800">
        <PaymentHeader
          transactionCount={displayTransactions.length}
          feeSummary={feeSummary}
          syncing={syncing}
          loading={loading}
          isMobile={isMobile}
          paidLinkExists={paidLinkExists}
          canCreatePaymentLink={!isDuplicate && canCreatePaymentLink}
          paymentLinkBlockedReason={paymentLinkBlockedReason}
          onRefresh={() => handleSyncAll()}
          onCreatePaymentLink={handleOpenPaymentLinkModal}
          isStudent={leadData?.IsStudent}
          isReadOnly={isDuplicate}
        />
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 -mx-4 px-4 pb-8 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-dark-700">
        {displayTransactions.length === 0 ? (
          <EmptyPaymentState
            isMobile={isMobile}
            isFeePayment={isFeePayment}
            paidLinkExists={paidLinkExists}
            canCreatePaymentLink={!isDuplicate && canCreatePaymentLink}
            paymentLinkBlockedReason={paymentLinkBlockedReason}
            onCreatePaymentLink={handleOpenPaymentLinkModal}
            isStudent={leadData?.IsStudent}
            feeSummary={feeSummary}
            isReadOnly={isDuplicate}
          />
        ) : (
          <div className="space-y-3">
            {displayTransactions.map((transaction) => (
              <PaymentLinkCard
                key={transaction.PaymentLinkId}
                transaction={transaction}
                isFeePayment={isFeePayment}
                isMobile={isMobile}
                isReadOnly={isDuplicate}
                isCancelling={isCancellingLink === transaction.PaymentLinkId}
                isSyncing={!!transactionSyncing[transaction.LinkId]}
                onCancel={isDuplicate ? undefined : handleOpenCancelConfirm}
                onSync={isDuplicate ? undefined : handleSyncTransaction}
                onDownloadInvoice={handleDownloadInvoice}
                onDownloadReceipt={handleDownloadReceipt}
                isDownloadingInvoice={isDownloadingInvoice === transaction.PaymentLinkId}
                isStudent={leadData?.IsStudent}
                onEdit={isDuplicate ? undefined : handleEditPayment}
                onDelete={isDuplicate ? undefined : handleOpenDeleteConfirm}
                onVerify={!isDuplicate && isAdmin ? handleOpenVerifyModal : undefined}
              />
            ))}
          </div>
        )}
      </div>

      <PaymentLinkModal
        isFeePayment={isFeePayment}
        isOpen={paymentLinkModal.isOpen}
        paymentLinkStep={paymentLinkModal.paymentLinkStep}
        gateways={paymentLinkModal.gateways}
        isLoadingGateways={paymentLinkModal.isLoadingGateways}
        selectedGateway={paymentLinkModal.selectedGateway}
        paymentCatalogs={paymentLinkModal.paymentCatalogs}
        isLoadingCatalogs={paymentLinkModal.isLoadingCatalogs}
        selectedCatalog={paymentLinkModal.selectedCatalog}
        applicationForms={paymentLinkModal.applicationForms}
        isLoadingApplicationForms={paymentLinkModal.isLoadingApplicationForms}
        selectedApplicationForm={paymentLinkModal.selectedApplicationForm}
        paymentFormData={paymentLinkModal.paymentFormData}
        isCreatingLink={paymentLinkModal.isCreatingLink}
        createdLinkData={paymentLinkModal.createdLinkData}
        copySuccess={paymentLinkModal.copySuccess}
        handleClose={paymentLinkModal.handleClose}
        handleSelectGateway={paymentLinkModal.handleSelectGateway}
        handleSelectCatalog={paymentLinkModal.handleSelectCatalog}
        handleInputChange={paymentLinkModal.handleInputChange}
        handleBulkUpdate={paymentLinkModal.handleBulkUpdate}
        handleCreatePaymentLink={paymentLinkModal.handleCreatePaymentLink}
        handleCopyPaymentLink={paymentLinkModal.handleCopyPaymentLink}
        setPaymentLinkStep={paymentLinkModal.setPaymentLinkStep}
        setSelectedApplicationForm={paymentLinkModal.setSelectedApplicationForm}
        isMobile={isMobile}
        isInternational={isInternational}
        leadData={leadData}
        feeBreakup={paymentLinkModal.feeBreakup}
        setFeeBreakup={paymentLinkModal.setFeeBreakup}
      />

      <PaymentDetailsModal
        isOpen={isPaymentModalOpen}
        onClose={(result) => {
          setIsPaymentModalOpen(false);
          setEditingPayment(null);
          loadPaymentLinks(); // Refresh links after adding payment

          if (result?.success) {
            if (refreshLeadData) refreshLeadData();
            if (onPaymentSuccess) onPaymentSuccess(result.formId);

            // If formId is missing (common for Cash payments), fallback to the first available form
            // const finalFormId = result.formId || (applicationForms.length > 0 ? applicationForms[0].FormId : null);

            // if (onPaymentSuccess) onPaymentSuccess(finalFormId);
          }
        }}
        leadId={leadId}
        payment={editingPayment}
        transactions={transactions}
        pendingAmount={feeSummary?.pendingFees}
      />

      <CancelPaymentModal
        show={showCancelConfirmModal}
        transaction={selectedLinkToCancel}
        confirmState={cancelConfirmState}
        isLoading={isCancellingLink !== null}
        onConfirm={handleConfirmCancelPayment}
        onClose={handleCloseCancelConfirm}
      />

      <DeletePaymentModal
        show={showDeleteConfirmModal}
        transaction={selectedLinkToDelete}
        confirmState={deleteConfirmState}
        isLoading={isDeletingLink !== null}
        onConfirm={handleConfirmDeletePayment}
        onClose={handleCloseDeleteConfirm}
      />

      <PaymentReceiptModal
        isOpen={isReceiptModalOpen}
        onClose={() => setIsReceiptModalOpen(false)}
        transaction={selectedReceiptTransaction}
        receiptType={receiptType}
        leadId={leadId}
      />

      <PaymentVerifyModal
        isOpen={isVerifyModalOpen}
        onClose={() => { setIsVerifyModalOpen(false); setVerifyModalLead(null); }}
        lead={verifyModalLead}
        onActionComplete={handleVerifyModalActionComplete}
      />
    </div>
  );
};

export default PaymentDetailsTab;
